import {
	AlertTriangle,
	ArrowBigRight,
	ArrowBigRightDash,
	ArrowDown,
	ArrowLeftRight,
	ArrowRightLeft,
	ArrowRightToLine,
	ArrowUp,
	ArrowUpDown,
	ArrowUpRight,
	BadgeCheck,
	BarChart2,
	CheckCheck,
	CircleArrowDown,
	CircleArrowRight,
	CircleArrowUp,
	CircleCheck,
	CircleDot,
	CircleDotDashed,
	CircleOff,
	Crosshair,
	FastForward,
	Flag,
	Goal,
	Hand,
	MoveRight,
	Percent,
	RefreshCcw,
	RotateCcw,
	Ruler,
	Shield,
	ShieldAlert,
	ShieldBan,
	ShieldCheck,
	ShieldHalf,
	SquareArrowRight,
	SquareArrowUpRight,
	Star,
	Target,
	Zap,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { Badge, ButtonTabs } from "ui-kit";
import { useImageSrcWithFallback } from "../../../hooks/useImageSrcWithFallback";
// @ts-ignore
import clsx from "clsx";
import { _, _any } from "i18n";
import type { Keys } from "i18n/locales/types";
import { FilterValue } from "react-table";
import clubPlaceholder from "ui-kit/assets/images/logo_fixtures.png";
import type { LinedTabsItem } from "ui-kit/src/LinedTabs/LinedTabs";
import Loader from "ui-kit/src/Loader/Loader";
import Modal from "ui-kit/src/Modal";
import { useVideoPlayer } from "../../Contexts/contexts";
import { setVideoTime } from "../../functions";
import { useStatsPeriodSelection } from "../../hooks/useStatsPeriodSelection";
import { useGameStore } from "../../store";
import {
	EventFilterValue,
	GameStatsExportCellRequest,
	GameStatsExportRequest,
	NavOptions,
	ProcessingLevelEnum,
	TeamSide,
	VideoSidebar,
	type GamePageLazyResponse,
	type GamePageResponse,
} from "../../types";
import { getTaggedTeamSide, isOneTeamTagged } from "../../utils/teamTagged";
import OneTeamTaggingDisclaimer from "../OneTeamTaggingDisclaimer";
import { StatsLinedTabsAdapter } from "../StatsLinedTabsAdapter";
import "./TeamStats.css";
import { TeamStatsExport } from "./TeamStatsExport";
import TeamStatsItem from "./TeamStatsItem";
import { CategoryEnum, TeamStatCalculationResult, TeamStatType, getTeamStatDefinitionByCategory } from "./definition";
import { calculateTeamStats } from "./functions";

const iconSize = 20;

const OTT_STAT_ICONS: Partial<Record<TeamStatType, ReactNode>> = {
	// Summary
	[TeamStatType.Goal]: <Zap size={iconSize} />,
	[TeamStatType.GoalOwn]: <Zap size={iconSize} />,
	[TeamStatType.Shot]: <Crosshair size={iconSize} />,
	[TeamStatType.Foul]: <ShieldAlert size={iconSize} />,
	[TeamStatType.Offside]: <Target size={iconSize} />,
	[TeamStatType.FreeKick]: <Target size={iconSize} />,
	[TeamStatType.Corner]: <Flag size={iconSize} />,
	[TeamStatType.Possession]: <RotateCcw size={iconSize} />,
	[TeamStatType.AverageRating]: <Star size={iconSize} />,

	// Offensive
	[TeamStatType.ShotOnTarget]: <Target size={iconSize} />,
	[TeamStatType.ShotOffTarget]: <CircleOff size={iconSize} />,
	[TeamStatType.ShotBlocked]: <Shield size={iconSize} />,
	[TeamStatType.ShotInsidePa]: <CircleDot size={iconSize} />,
	[TeamStatType.ShotOutsidePa]: <CircleDotDashed size={iconSize} />,
	[TeamStatType.ShotConversion]: <Percent size={iconSize} />,
	[TeamStatType.TakeOn]: <FastForward size={iconSize} />,
	[TeamStatType.TakeOnSuccess]: <FastForward size={iconSize} />,

	// Defensive
	[TeamStatType.TackleSuccess]: <ShieldCheck size={iconSize} />,
	[TeamStatType.Tackle]: <ShieldHalf size={iconSize} />,
	[TeamStatType.AerialDuel]: <ArrowUpDown size={iconSize} />,
	[TeamStatType.Interception]: <Hand size={iconSize} />,
	[TeamStatType.Clearance]: <ArrowUp size={iconSize} />,
	[TeamStatType.Block]: <ShieldBan size={iconSize} />,
	[TeamStatType.Mistake]: <AlertTriangle size={iconSize} />,
	[TeamStatType.Recovery]: <RefreshCcw size={iconSize} />,

	// Distribution
	[TeamStatType.PassSuccess]: <CheckCheck size={iconSize} />,
	[TeamStatType.Pass]: <MoveRight size={iconSize} />,
	[TeamStatType.PassAccuracy]: <Percent size={iconSize} />,
	[TeamStatType.KeyPass]: <Star size={iconSize} />,
	[TeamStatType.PassInFinalThirdSuccess]: <BadgeCheck size={iconSize} />,
	[TeamStatType.PassInFinalThird]: <Goal size={iconSize} />,
	[TeamStatType.PassInMiddleThirdSuccess]: <CircleCheck size={iconSize} />,
	[TeamStatType.PassInMiddleThird]: <CircleDot size={iconSize} />,
	[TeamStatType.PassInDefensiveThirdSuccess]: <ShieldCheck size={iconSize} />,
	[TeamStatType.PassInDefensiveThird]: <Shield size={iconSize} />,
	[TeamStatType.PassLongSuccess]: <ArrowBigRightDash size={iconSize} />,
	[TeamStatType.PassLong]: <ArrowBigRight size={iconSize} />,
	[TeamStatType.PassMediumSuccess]: <SquareArrowRight size={iconSize} />,
	[TeamStatType.PassMedium]: <Ruler size={iconSize} />,
	[TeamStatType.PassShortSuccess]: <CircleArrowRight size={iconSize} />,
	[TeamStatType.PassShort]: <ArrowRightToLine size={iconSize} />,
	[TeamStatType.PassForwardSuccess]: <CircleArrowUp size={iconSize} />,
	[TeamStatType.PassForward]: <ArrowUp size={iconSize} />,
	[TeamStatType.PassSidewaysSuccess]: <ArrowRightLeft size={iconSize} />,
	[TeamStatType.PassSideways]: <ArrowLeftRight size={iconSize} />,
	[TeamStatType.PassBackwardSuccess]: <CircleArrowDown size={iconSize} />,
	[TeamStatType.PassBackward]: <ArrowDown size={iconSize} />,
	[TeamStatType.CrossSuccess]: <SquareArrowUpRight size={iconSize} />,
	[TeamStatType.Cross]: <ArrowUpRight size={iconSize} />,
};

interface GameDataProps {
	gameData: GamePageResponse;
	gameDataLazy?: GamePageLazyResponse;
	currentNavOption: NavOptions;
	tabs: LinedTabsItem[];
	handleChangeNavOptions: (key: NavOptions) => void;
	handleGameStatsExport: (tableExportRequest: GameStatsExportRequest) => void;
}

export function TeamStats({ gameData, gameDataLazy, tabs, currentNavOption, handleChangeNavOptions, handleGameStatsExport }: GameDataProps) {
	const { setFilterValues, setFilteredFilterValues, setSidebar } = useGameStore();
	const { playerRef, setIsPlaying } = useVideoPlayer();
	const [category, setCategory] = useState<CategoryEnum>(CategoryEnum.Summary);
	const [modalData, setModalData] = useState<TeamStatCalculationResult | null>(null);
	const [exportModal, setExportModal] = useState<boolean>(false);
	const { eventFilter, periodTabs, periods, isEventFilterHydrated, handlePeriodsChange } = useStatsPeriodSelection(
		gameData.game.id,
	);

	const teamTagged = gameData?.game?.teamTagged ?? null;
	const taggedSide = getTaggedTeamSide(teamTagged);
	const isOtt = isOneTeamTagged(teamTagged);

	const handleOnCategoryChange = (keys: (string | number)[]) => {
		setCategory(keys[0] as CategoryEnum);
	};

	const handleOnShowModal = (calculationResult: TeamStatCalculationResult) => {
		setModalData(calculationResult);
	};

	const handleOnPlayEvents = (type: "home" | "away" | "all", statType?: TeamStatType) => {
		if (!eventFilter) return;
		if (modalData && modalData.filterValues.length > 0) {
			let filterValues: EventFilterValue[] = [];
			const playType = type === "all" && taggedSide !== null ? taggedSide : type;

			if (playType === "home") {
				filterValues = eventFilter.mutateFilterValues(filterValues, "team.home", String(modalData.homeGameTeamId));
			} else if (playType === "away") {
				filterValues = eventFilter.mutateFilterValues(filterValues, "team.away", String(modalData.awayGameTeamId));
			}

			if (statType === undefined) {
				modalData.filterValues.forEach((fv) => {
					filterValues = eventFilter.mutateFilterValues(filterValues, fv.key, fv.value.pop() as string);
				});
			} else if (playType !== "all") {
				let filterValuesSource: FilterValue[] | undefined;
				switch (true) {
					case modalData.goalFilterValues && statType === TeamStatType.Goal:
						filterValuesSource = modalData.goalFilterValues;
						break;
					case modalData.ownGoalFilterValues && statType === TeamStatType.GoalOwn:
						filterValuesSource = modalData.ownGoalFilterValues;
						break;
				}

				if (filterValuesSource) {
					filterValuesSource.forEach((fv) => {
						filterValues = eventFilter.mutateFilterValues(filterValues, fv.key, fv.value.pop() as string);
					});
				}
			}

			setFilterValues(filterValues);
			setFilteredFilterValues(filterValues);
			handleChangeNavOptions(NavOptions.FullGame);
			setSidebar(VideoSidebar.EventList);
			setVideoTime(playerRef);
			setIsPlaying(true);
		}
	};

	const handleOnExportTeamStats = (format: "csv" | "xlsx" | "json", categories: CategoryEnum[], periods: string[]) => {
		if (!eventFilter) return;

		const header = [
			{ key: "category", value: _(`gamePage.teamStats.export.column.category`) },
			{ key: "type", value: _(`gamePage.teamStats.export.column.statistic`) },
		];

		if (isOtt === false || taggedSide === "home") {
			header.push({ key: "home", value: gameData.homeGameTeam.clubName ?? "" });
		}

		if (isOtt === false || taggedSide === "away") {
			header.push({ key: "away", value: gameData.awayGameTeam.clubName ?? "" });
		}

		const request = {
			type: "teamStats",
			name: `game-${gameData.game.id}-team-stats`,
			format: format,
			header,
			rows: [],
		} as GameStatsExportRequest;

		const processingLevel = gameData?.game?.processingLevel as ProcessingLevelEnum | undefined;

		categories.forEach((c) => {
			getTeamStatDefinitionByCategory(c, gameData.permissions.showRatings, processingLevel, teamTagged).forEach((t) => {
				const result = calculateTeamStats(eventFilter, gameData, t, periods, gameDataLazy);
				const row: GameStatsExportCellRequest[] = [
					{ key: "category", value: _(`gamePage.stats.category.${c}`) },
					{ key: "type", value: _(`gamePage.teamStats.type.${t}`) },
				];

				if (isOtt === false || taggedSide === "home") {
					row.push({ key: "home", value: result.homeValueFormatted });
				}

				if (isOtt === false || taggedSide === "away") {
					row.push({ key: "away", value: result.awayValueFormatted });
				}

				(request.rows as unknown as GameStatsExportCellRequest[][]).push(row);
			});
		});

		handleGameStatsExport(request);
		setExportModal(false);
	};

	const categoryTabs = Object.values(CategoryEnum).map((cat) => ({
		key: cat,
		label: _(`game.stats.menu.${cat}` as keyof Keys),
	}));

	const processingLevel = gameData?.game?.processingLevel as ProcessingLevelEnum | undefined;
	const displayData = getTeamStatDefinitionByCategory(category, gameData.permissions.showRatings, processingLevel, teamTagged);

	const { imgSrc: homeStatsClubLogoSrc, onImgError: onHomeStatsClubLogoError } = useImageSrcWithFallback({
		sourceKey: gameData.homeGameTeam?.club?.logo ?? null,
		fallbackSrc: clubPlaceholder,
	});
	const { imgSrc: awayStatsClubLogoSrc, onImgError: onAwayStatsClubLogoError } = useImageSrcWithFallback({
		sourceKey: gameData.awayGameTeam?.club?.logo ?? null,
		fallbackSrc: clubPlaceholder,
	});

	const taggedTeam = taggedSide === "away" ? gameData.awayGameTeam : gameData.homeGameTeam;
	const taggedLogoSrc = taggedSide === "away" ? awayStatsClubLogoSrc : homeStatsClubLogoSrc;
	const onTaggedLogoError = taggedSide === "away" ? onAwayStatsClubLogoError : onHomeStatsClubLogoError;

	const NonGoalContent = ({ modalData }: { modalData: TeamStatCalculationResult }) => {
		if (modalData?.isDescriptionOnly) {
			return (
				<>
					<div className="team-stat-modal__description">{_any(modalData.description)}</div>
				</>
			);
		}

		const showHome = isOtt === false || taggedSide === "home";
		const showAway = isOtt === false || taggedSide === "away";

		return (
			<>
				{showHome && (
					<Badge
						label={`${_(`gamePage.teamStats.playHome`)} (${modalData.homeValue})`}
						className="team-stat-modal__team-button"
						size="md"
						variant="secondary"
						icon="play"
						disabled={modalData.homeValue === 0}
						onClick={() => handleOnPlayEvents(TeamSide.Home)}
					/>
				)}
				{showAway && (
					<Badge
						label={`${_(`gamePage.teamStats.playAway`)} (${modalData.awayValue})`}
						className="team-stat-modal__team-button"
						size="md"
						variant="primary"
						icon="play"
						disabled={modalData.awayValue === 0}
						onClick={() => handleOnPlayEvents(TeamSide.Away)}
					/>
				)}
			</>
		);
	};

	const playAllValue =
		taggedSide === "home" ? modalData?.homeValue ?? 0 : taggedSide === "away" ? modalData?.awayValue ?? 0 : modalData?.allValue ?? 0;

	return (
		<>
			<StatsLinedTabsAdapter tabs={tabs} active={currentNavOption} onChange={handleChangeNavOptions}>
				{gameData.permissions.downloadTeamStats && (
					<Badge
						label={_(`gamePage.teamStats.export.button`)}
						variant="primary"
						icon="plus-white"
						size="md"
						onClick={() => setExportModal(true)}
					/>
				)}
			</StatsLinedTabsAdapter>
			<div className="stats-container">
				<div className="game-data__nav">
					<ButtonTabs items={categoryTabs} active={[category]} showCheck={true} onChange={handleOnCategoryChange} />
					<ButtonTabs items={periodTabs} active={periods} showCheck={true} onChange={handlePeriodsChange} isMultiple={true} />
				</div>
				{isOtt && <OneTeamTaggingDisclaimer />}
				<div>
					{isOtt === false && (
						<div className={clsx("team-stats-header", { "team-stats-header--one-team": isOtt })}>
							<div className="team-stats-header__team">
								<img
									src={homeStatsClubLogoSrc ?? clubPlaceholder}
									alt={gameData.homeGameTeam.clubName || ""}
									onError={onHomeStatsClubLogoError}
								/>
								<span>{gameData.homeGameTeam.clubName || ""}</span>
							</div>
							<div className="team-stats-header__title">{_(`game.stats.menu.${category.toString()}` as keyof Keys)}</div>
							<div className="team-stats-header__team">
								<img
									src={awayStatsClubLogoSrc ?? clubPlaceholder}
									alt={gameData.awayGameTeam.clubName || ""}
									onError={onAwayStatsClubLogoError}
								/>
								<span>{gameData.awayGameTeam.clubName || ""}</span>
							</div>
						</div>
					)}
					<div className={clsx("team-stats-body", { "team-stats-body--one-team": isOtt })}>
						{isEventFilterHydrated === false ? (
							<Loader />
						) : isOtt ? (
							<div className="ott-stat-cards">
								{displayData?.map((statType, i) => {
									const calc = calculateTeamStats(eventFilter, gameData, statType, periods, gameDataLazy);
									const value = taggedSide === "away" ? calc.awayValueFormatted : calc.homeValueFormatted;
									const isClickable = gameData.permissions.showEvents && calc.isFilterable;
									return (
										<div
											key={`ott-card-${i}`}
											className={clsx("ott-stat-card", { "ott-stat-card--clickable": isClickable })}
											onClick={isClickable ? () => handleOnShowModal(calc) : undefined}
										>
											<div className="ott-stat-card__icon">{OTT_STAT_ICONS[statType] ?? <BarChart2 size={20} />}</div>
											<div className="ott-stat-card__label">{_(calc.label as keyof Keys)}</div>
											<div className="ott-stat-card__value">{value}</div>
										</div>
									);
								})}
							</div>
						) : (
							displayData?.map((statType, i) => (
								<TeamStatsItem
									key={`stat-item-${i}`}
									gameData={gameData}
									gameDataLazy={gameDataLazy}
									statType={statType}
									periods={periods}
									taggedSide={taggedSide}
									handleShowModal={handleOnShowModal}
								/>
							))
						)}
					</div>
				</div>
				{modalData !== null && (
					<Modal title={_(modalData.label as keyof Keys)} show={true} size="lg" onClose={() => setModalData(null)}>
						<div className={clsx("team-stat-modal", { "team-stat-modal--one-team": isOtt })}>
							{modalData.isGoal ? (
								<>
									{(isOtt === false || taggedSide === "home") && (
										<>
											<Badge
												label={`${_(`gamePage.teamStats.playHomeGoal`)} (${modalData.homeGoalEvents?.length || 0})`}
												className="team-stat-modal__team-button"
												size="md"
												variant="secondary"
												icon="play"
												disabled={modalData.homeGoalEvents && modalData.homeGoalEvents.length === 0}
												onClick={() => handleOnPlayEvents(TeamSide.Home, TeamStatType.Goal)}
											/>
											<Badge
												label={`${_(`gamePage.teamStats.playHomeGoalOwn`)} (${modalData.homeOwnGoalEvents?.length || 0})`}
												className="team-stat-modal__team-button"
												size="md"
												variant="secondary"
												icon="play"
												disabled={modalData.homeOwnGoalEvents && modalData.homeOwnGoalEvents.length === 0}
												onClick={() => handleOnPlayEvents(TeamSide.Home, TeamStatType.GoalOwn)}
											/>
										</>
									)}
									{(isOtt === false || taggedSide === "away") && (
										<>
											<Badge
												label={`${_(`gamePage.teamStats.playAwayGoal`)} (${modalData.awayGoalEvents?.length || 0})`}
												className="team-stat-modal__team-button"
												size="md"
												variant="primary"
												icon="play"
												disabled={modalData.awayGoalEvents && modalData.awayGoalEvents.length === 0}
												onClick={() => handleOnPlayEvents(TeamSide.Away, TeamStatType.Goal)}
											/>
											<Badge
												label={`${_(`gamePage.teamStats.playAwayGoalOwn`)} (${modalData.awayOwnGoalEvents?.length || 0})`}
												className="team-stat-modal__team-button"
												size="md"
												variant="primary"
												icon="play"
												disabled={modalData.awayOwnGoalEvents && modalData.awayOwnGoalEvents.length === 0}
												onClick={() => handleOnPlayEvents(TeamSide.Away, TeamStatType.GoalOwn)}
											/>
										</>
									)}
								</>
							) : (
								<NonGoalContent modalData={modalData} />
							)}
							{!modalData.isDescriptionOnly && isOtt === false && (
								<Badge
									label={`${_(`gamePage.teamStats.playAll`)} (${playAllValue})`}
									className="team-stat-modal__game-button"
									size="md"
									variant="default"
									icon="play"
									disabled={playAllValue === 0}
									onClick={() => handleOnPlayEvents("all")}
								/>
							)}
						</div>
					</Modal>
				)}
				<Modal title={_(`gamePage.teamStats.export.button`)} show={exportModal} size="xs" onClose={() => setExportModal(false)}>
					<TeamStatsExport
						category={category}
						gamePeriods={periodTabs}
						selectedPeriods={periods}
						onExportTeamStats={handleOnExportTeamStats}
					/>
				</Modal>
			</div>
		</>
	);
}
